/* The audit ledger.

   **A chain, not a grid.** Design rule 3 names *"a chain of evidence"* as one of the shapes a
   primary view may take, and the link is what makes it one: every entry prints the hash it links
   back to, on its own line, and the rail beside the list ties each entry to the one below it, so the
   structure a reader is being asked to trust is the structure they are looking at. A table of the
   same values would show the entries and hide the chain.

   Tokens only (ADR-0007) — stylelint fails a colour literal, because a literal is a colour that
   will not change when the theme does. Nothing below 12px (`--t-min`). */

.whoCan {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel-2);
  font-size: var(--t-dense);
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 68ch;
}

.whoCan strong {
  display: block;
  color: var(--ink);
  margin-bottom: 4px;
}

.note {
  margin: 0 0 10px;
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.5;
  max-width: 80ch;
}

/* ------------------------------------------------------------------ the page's own layout */

.stack {
  grid-column: 1 / -1;
  display: grid;
  gap: var(--gap);
  min-width: 0;
}

.facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

@media (width <= 1100px) {
  .facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (width <= 560px) {
  .facts {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ------------------------------------------------------------------ what the chain proves */

.check {
  display: grid;
  gap: 12px;
}

.answers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--gap);
}

@media (width <= 760px) {
  .answers {
    grid-template-columns: minmax(0, 1fr);
  }
}

.answer {
  display: grid;
  align-content: start;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--panel-2);
  min-width: 0;
}

.question {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

/* Colour never carries the meaning on its own: each answer is a `StatusPill` (icon plus words) and
   a sentence beside it. */
.verdict,
.anchor {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: 0;
  font-size: var(--t-dense);
  color: var(--ink);
}

.verdictRange,
.anchorState {
  flex-basis: 100%;
  font-size: var(--t-min);
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

.caveats {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.caveat {
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.5;
  padding-left: 10px;
  border-left: 2px solid var(--warn-line);
  max-width: 100ch;
}

/* ----------------------------------------------------------------------------- the chain */

.whole {
  margin: 0 0 10px;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.gap {
  margin: 0 0 10px;
  padding: 10px 12px;
  border: 1px solid var(--crit-line);
  border-radius: 8px;
  background: var(--crit-w);
  font-size: var(--t-dense);
  color: var(--ink);
  line-height: 1.5;
}

/* **The rail.** Each entry is a node and the line below it runs to the node of the entry it links
   back to, so a reader sees one thread with every entry tied to the one before. The line is the
   accent tint (chrome, not a status) and stops at the first entry ever recorded. */
.chain {
  list-style: none;
  margin: 0;
  padding: 0 0 0 22px;
  display: grid;
  gap: 10px;
}

.entry {
  position: relative;
  display: grid;
  gap: 5px;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  min-width: 0;
}

.entry::before {
  content: "";
  position: absolute;
  left: -20px;
  top: 14px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 3px var(--panel);
}

.entry::after {
  content: "";
  position: absolute;
  left: -16px;
  top: 24px;
  bottom: -24px;
  width: 2px;
  background: var(--accent-line);
}

.entryLast::after {
  display: none;
}

.entryHead {
  display: flex;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
  min-width: 0;
}

.seq {
  font-size: var(--t-dense);
  font-weight: 700;
  color: var(--ink);
  min-width: 4ch;
}

.seqLink {
  display: inline-flex;
  align-items: center;
  min-width: var(--target-min);
  min-height: var(--target-min);
  color: var(--accent);
  text-decoration: none;
}

.seqLink:hover,
.seqLink:focus-visible {
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.type {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--accent);
}

.about,
.when,
.actor {
  display: flex;
  gap: 5px;
  align-items: baseline;
  font-size: var(--t-min);
  color: var(--ink-2);
  min-width: 0;
  flex-wrap: wrap;
}

.aboutId,
.actorId,
.time {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}

.actor {
  margin-left: auto;
}

.entryLink {
  display: flex;
  gap: 8px;
  align-items: baseline;
  flex-wrap: wrap;
}

.linkIcon {
  flex-shrink: 0;
  align-self: center;
  color: var(--muted);
}

.hash {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink-2);
}

.linkWord {
  font-size: var(--t-min);
  color: var(--muted);
}

.receipt,
.payloadToggle {
  min-height: 24px;
  background: none;
  border: 1px solid var(--line-2);
  border-radius: 6px;
  padding: 2px 8px;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
}

.receipt:hover,
.payloadToggle:hover {
  background: var(--accent-w);
}

.receipt:focus-visible,
.payloadToggle:focus-visible,
.pagerButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.payloadToggle {
  margin-left: auto;
}

.payload {
  display: grid;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--panel-2);
}

.payloadNote,
.undeclared {
  margin: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.5;
}

.pairs {
  margin: 0;
  display: grid;
  gap: 3px;
}

.pair {
  display: grid;
  grid-template-columns: minmax(10ch, 22ch) 1fr;
  gap: 8px;
}

/* A free-form value is a paragraph rather than a cell: `applicability_determined.reasoning` runs
   past 300 characters, and squeezing it into a column built for a UUID would make the one payload
   value that explains a determination the one nobody reads. */
.prose {
  grid-template-columns: 1fr;
}

.key {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}

.value {
  margin: 0;
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink);
  overflow-wrap: anywhere;
  line-height: 1.5;
}

/* ----------------------------------------------------------------------------- the pager */

.pager {
  display: flex;
  gap: 10px;
  align-items: baseline;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-left: 22px;
}

.pagerButton {
  background: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 6px;
  padding: 4px 10px;
  font: inherit;
  font-size: var(--t-min);
  color: var(--on-fill);
  cursor: pointer;
}

.pagerButton:hover {
  background: var(--accent-h);
}

.pagerEnd {
  font-size: var(--t-min);
  color: var(--ink-2);
}

/* A row's own buttons in the Evidence column. */
.rowButtons {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
}

/* The Evidence cell's two buttons share one line (they were stacked, which made every row of the
   ledger 65px: U6, 2026-11-03). */
.buttonColumn {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  white-space: nowrap;
}
